import { TransactionSource } from '@signalvote/shared';
import { getCampaignTodayString } from '@/lib/calendarUtils';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// ── Date helpers (shared by the close/ zones + the dashboard) ───────────────
// Every date input is sliced to its leading YYYY-MM-DD (so full ISO timestamps
// are safe) and parsed at local noon so the day-of-month is DST-stable. "Today"
// anchors to campaign timezone via getCampaignTodayString(), never the browser
// wall clock (ADR-017 / root golden rule).

/** "Jun 3" — short month + day. Accepts a date string or full ISO timestamp. */
export function fmtDateShort(s: string): string {
  return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/** "Jun 3, 2026" — short month + day + year. Accepts a date string or ISO timestamp. */
export function fmtDateLong(s: string): string {
  return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The receipt-time clause appended to a rendered due date, when the jurisdiction publishes one
 * (Mississippi: " by 5:00 p.m."). Empty for every jurisdiction that declares no time. One helper so
 * every surface that renders a due date joins the time the same way.
 */
export function dueTimeSuffix(dueTime: string | null | undefined): string {
  return dueTime ? ` by ${dueTime}` : '';
}

/** Whole-day delta from campaign-today to `iso` (negative = past due). */
export function daysUntil(iso: string): number {
  const due = new Date(iso + (iso.includes('T') ? '' : 'T12:00:00'));
  const today = new Date(getCampaignTodayString() + 'T12:00:00');
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

/**
 * A report's display name without its trailing period descriptor. Report names
 * arrive as "{type} — {period}" (e.g. "Weekly Pre-Election Report — week of
 * Aug 15"). The close header now shows the period separately ("Covers …"), so
 * the H1 shows just the type. Splits on the first em-dash separator and returns
 * the head; names without the suffix pass through unchanged.
 */
export function reportTypeName(name: string): string {
  const sep = name.indexOf(' — ');
  return sep === -1 ? name : name.slice(0, sep).trim();
}

export function formatAmount(amount: number): string {
  if (amount < 0) return `-${currencyFormatter.format(Math.abs(amount))}`;
  return currencyFormatter.format(amount);
}

export function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "2026-08-17" → "Aug 17". A calendar date, so no timezone shift. */
export function formatMonthDay(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// Bug 4: ImportBatchHistoryItem.uploadedAt is a full ISO timestamp, not a
// calendar date. Don't pipe it through formatDate (which splits on '-' and
// produces NaN on the time portion). When .NET serializes a Kind=Unspecified
// DateTime without a 'Z' suffix, append one so it parses as UTC.
export function formatDateTime(iso: string): string {
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z';
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function sourceLabel(source: TransactionSource): string {
  switch (source) {
    case TransactionSource.CsvImport: return 'CSV';
    case TransactionSource.ManualEntry: return 'Manual';
    case TransactionSource.Plaid:
    case TransactionSource.Square:
    case TransactionSource.PayPal:
    case TransactionSource.CSV:
    case TransactionSource.Manual:
    default:
      return String(source);
  }
}
